/**
 * Betrieb, Personen und Kunden des Demonstrationsstands.
 *
 * **Alles frei erfunden.** Kein Name, keine Anschrift, keine Nummer stammt aus
 * einem echten Vorgang — `AGENTS.md` verbietet personenbezogene Realdaten im
 * Repository, und `FIX-001` ist ausdrücklich der *andere* Korpus: anonymisierte
 * echte Fälle, die außerhalb des Repositories liegen. Was hier steht, ist
 * synthetisch und darf deshalb versioniert werden.
 *
 * Die Orte sind trotzdem echte Orte in Oberfranken. Das ist kein Widerspruch:
 * Eine Tourenplanung, deren Adressen 400 km auseinanderliegen, zeigt nicht,
 * ob die Tagesansicht taugt. Fahrtzeiten zwischen Bamberg, Hallstadt und
 * Burgebrach sind plausibel, und genau das soll man beim Durchklicken
 * beurteilen können.
 */

import type { Kunde, Person } from "./modell.js";
import { KUNDE, PERSON } from "./kennungen.js";
import { tag } from "./zeit.js";

export const BETRIEB = {
  name: "Elektro Wagner GmbH",
  anschrift: {
    strasse: "Gewerbering 14",
    plz: "96114",
    ort: "Hirschaid",
  },
  telefon: "09543 440180",
  email: "info@elektro-wagner-demo.de",
} as const;

/**
 * Sechs Personen — die Größe, die `CLAUDE.md` als Zielgruppe nennt (2–25
 * Benutzer), am unteren Rand.
 *
 * Die Rollenverteilung zeigt absichtlich die Personalunion: Der Inhaber ist
 * zugleich Administrator, die Bürokraft macht Disposition **und** Buchhaltung.
 * Ein Demostand mit sechs Menschen in sechs sauber getrennten Rollen würde
 * eine Trennung vorführen, die es in einem Betrieb dieser Größe nicht gibt —
 * und die Oberfläche nie an der Frage messen, ob eine Person mit drei Rollen
 * noch eine brauchbare Navigation sieht.
 */
export const PERSONEN: readonly Person[] = [
  {
    ref: PERSON.inhaber,
    name: "Andreas Wagner",
    kuerzel: "AW",
    rollen: ["tenant-admin", "office", "dispatcher"],
    email: "a.wagner@elektro-wagner-demo.de",
    telefon: "09543 440181",
    farbe: "#2f6f4f",
    verfuegbar: true,
  },
  {
    ref: PERSON.buero,
    name: "Sabine Kern",
    kuerzel: "SK",
    rollen: ["office", "dispatcher", "accounting"],
    email: "s.kern@elektro-wagner-demo.de",
    telefon: "09543 440182",
    farbe: "#4a5a8a",
    verfuegbar: true,
  },
  {
    ref: PERSON.monteurA,
    name: "Marek Liszka",
    kuerzel: "ML",
    rollen: ["technician"],
    email: "m.liszka@elektro-wagner-demo.de",
    telefon: "0151 20030041",
    farbe: "#8a5a2f",
    verfuegbar: true,
  },
  {
    ref: PERSON.monteurB,
    name: "Tobias Reuther",
    kuerzel: "TR",
    rollen: ["technician"],
    email: "t.reuther@elektro-wagner-demo.de",
    telefon: "0151 20030042",
    farbe: "#2f6a8a",
    verfuegbar: true,
  },
  {
    // Ein abwesender Monteur gehört in den Bestand, nicht als Sonderfall
    // nachträglich: `07` §7.4 fragt „welche Monteure sind verfügbar oder
    // verspätet". Eine Karte, die diese Frage nie mit „nein" beantworten muss,
    // ist beim Bauen nicht geprüft worden.
    ref: PERSON.monteurC,
    name: "Yusuf Demir",
    kuerzel: "YD",
    rollen: ["technician"],
    email: "y.demir@elektro-wagner-demo.de",
    telefon: "0151 20030043",
    farbe: "#6a4a7a",
    verfuegbar: false,
    abwesenheitsgrund: "krankgemeldet bis Freitag",
  },
  {
    // Die Steuerberaterin. Sie hat ein eigenes Konto mit engem Ausschnitt und
    // **keinen** MagicLink — `07` §7.2 begründet den Unterschied: Beides über
    // MagicLinks abzubilden würde die Sieben-Tage-Regel aus `05` §5.7
    // aushöhlen. Im Portal sieht sie deshalb weder Aufträge noch Kommunikation
    // noch Wissen; die Navigation blendet das schon aus (`navigation.ts`).
    ref: PERSON.partner,
    name: "Dr. Simone Ostermann",
    kuerzel: "SO",
    rollen: ["external-partner"],
    email: "ostermann@stb-ostermann-demo.de",
    verfuegbar: true,
  },
];

/**
 * Acht Kunden, bewusst ungleich.
 *
 * Zwei tragen eine externe Kundennummer aus der Branchensoftware, sechs nicht.
 * Das ist der Zustand, den `CLAUDE.md` beschreibt: Beim Kundenstamm ist die
 * Richtung noch offen, `PORTAL-002` führt die Felder deshalb von Anfang an
 * mit, „auch wenn sie leer bleiben". Ein Demostand, in dem alle acht eine
 * externe Nummer haben, würde eine Entscheidung vorführen, die nicht gefallen
 * ist — und einer, in dem keiner eine hat, würde die Spalte nie zeigen.
 */
export function kunden(bezugstag: Date): readonly Kunde[] {
  return [
    {
      ref: KUNDE.baeckerei,
      nummer: "K-1042",
      name: "Bäckerei Hufnagel GmbH",
      anschrift: { strasse: "Untere Königstraße 22", plz: "96052", ort: "Bamberg" },
      kontakte: [
        {
          name: "Bernd Hufnagel",
          funktion: "Inhaber",
          telefon: "0951 2077410",
          email: "b.hufnagel@baeckerei-hufnagel-demo.de",
          fuerMonteur: true,
        },
        {
          name: "Carola Hufnagel",
          funktion: "Buchhaltung",
          telefon: "0951 2077411",
          email: "buchhaltung@baeckerei-hufnagel-demo.de",
          // Die Buchhaltung gehört nicht auf jedes Monteurhandy — `07` §7.9
          // lädt nur die freigegebene Nummer vor.
          fuerMonteur: false,
        },
      ],
      externeKundennummer: "10042",
      herkunft: "branchensoftware",
      notiz: "Backstube läuft ab 03:00 Uhr. Arbeiten an der Kühlung nur nach Absprache.",
      seit: tag(bezugstag, -2190),
    },
    {
      ref: KUNDE.hausverwaltung,
      nummer: "K-1078",
      name: "Zeiler & Partner Hausverwaltung",
      anschrift: { strasse: "Luitpoldstraße 48", plz: "96052", ort: "Bamberg" },
      kontakte: [
        {
          name: "Ingrid Zeiler",
          funktion: "Objektbetreuung",
          telefon: "0951 3009120",
          email: "zeiler@zeiler-partner-demo.de",
          fuerMonteur: true,
        },
      ],
      externeKundennummer: "10078",
      herkunft: "branchensoftware",
      notiz: "Rechnungen immer je Objekt getrennt, nie gesammelt.",
      seit: tag(bezugstag, -1460),
    },
    {
      ref: KUNDE.autohaus,
      nummer: "K-1103",
      name: "Autohaus Merkle GmbH & Co. KG",
      anschrift: { strasse: "Industriestraße 7", plz: "96103", ort: "Hallstadt" },
      kontakte: [
        {
          name: "Ralf Merkle",
          funktion: "Werkstattleitung",
          telefon: "0951 7712030",
          email: "r.merkle@autohaus-merkle-demo.de",
          fuerMonteur: true,
        },
      ],
      notiz: "Hebebühnen dürfen nur außerhalb der Werkstattzeiten stromlos gemacht werden.",
      seit: tag(bezugstag, -1120),
    },
    {
      ref: KUNDE.privat,
      nummer: "K-1156",
      name: "Familie Brandt",
      anschrift: { strasse: "Am Steigerwaldblick 9", plz: "96138", ort: "Burgebrach" },
      kontakte: [
        {
          name: "Michael Brandt",
          telefon: "0170 4419922",
          email: "m.brandt-demo@posteo.de",
          fuerMonteur: true,
        },
      ],
      herkunft: "portal",
      notiz: "Hund im Garten. Vor Betreten klingeln.",
      seit: tag(bezugstag, -280),
    },
    {
      ref: KUNDE.seniorenheim,
      nummer: "K-1189",
      name: "Seniorenheim St. Elisabeth",
      anschrift: { strasse: "Kirchgasse 3", plz: "96047", ort: "Bamberg" },
      kontakte: [
        {
          name: "Schwester Adelheid",
          funktion: "Heimleitung",
          telefon: "0951 5540210",
          email: "leitung@st-elisabeth-demo.de",
          fuerMonteur: true,
        },
        {
          name: "Jörg Pfaff",
          funktion: "Haustechnik",
          telefon: "0951 5540218",
          fuerMonteur: true,
        },
      ],
      notiz:
        "Rufanlage ist sicherheitsrelevant. Ausfall länger als 30 Minuten muss der "
        + "Heimleitung gemeldet werden.",
      seit: tag(bezugstag, -2555),
    },
    {
      ref: KUNDE.metallbau,
      nummer: "K-1204",
      name: "Metallbau Grasser e.K.",
      anschrift: { strasse: "Forchheimer Straße 61", plz: "96129", ort: "Strullendorf" },
      kontakte: [
        {
          name: "Klaus Grasser",
          funktion: "Inhaber",
          telefon: "09543 882140",
          email: "info@metallbau-grasser-demo.de",
          fuerMonteur: true,
        },
      ],
      seit: tag(bezugstag, -640),
    },
    {
      ref: KUNDE.gemeinde,
      nummer: "K-1233",
      name: "Gemeinde Schönbrunn i. Steigerwald",
      anschrift: { strasse: "Rathausplatz 1", plz: "96185", ort: "Schönbrunn" },
      kontakte: [
        {
          name: "Petra Lindner",
          funktion: "Bauamt",
          telefon: "09546 92030",
          email: "bauamt@schoenbrunn-demo.de",
          fuerMonteur: false,
        },
      ],
      notiz:
        "Öffentlicher Auftraggeber: Rechnungen ausschließlich als XRechnung, "
        + "Leitweg-ID liegt in der Kundenakte.",
      seit: tag(bezugstag, -900),
    },
    {
      ref: KUNDE.praxis,
      nummer: "K-1251",
      name: "Praxis Dr. med. Hebling",
      anschrift: { strasse: "Hainstraße 12", plz: "96047", ort: "Bamberg" },
      kontakte: [
        {
          name: "Dr. Anne Hebling",
          telefon: "0951 2988440",
          email: "praxis@dr-hebling-demo.de",
          fuerMonteur: false,
        },
        {
          name: "Nadine Kraus",
          funktion: "Praxismanagement",
          telefon: "0951 2988441",
          email: "verwaltung@dr-hebling-demo.de",
          fuerMonteur: true,
        },
      ],
      herkunft: "portal",
      notiz: "Arbeiten nur mittwochnachmittags oder freitags nach 13:00.",
      seit: tag(bezugstag, -95),
    },
  ];
}
